import { useBilling } from '../contexts/BillingContext.js';
import { useIsMobileClient } from '../hooks/useTeamsContext.js';

/**
 * "Manage" link to the Stripe billing portal — cancel, change seats, update
 * card, download invoices.
 *
 * A paying customer must be able to reach this: marketplace reviewers check for
 * it, and "email us to cancel" is a poor answer to someone who wants out.
 *
 * It lives INSIDE the side-panel header row rather than in a strip of its own.
 * TrialBanner deliberately renders nothing once a tenant is paid, because the
 * panel's vertical budget is tight (see CLAUDE.md: every 10 px of chrome
 * matters, prefer inlining into an existing row). Paying customers are exactly
 * the people who should not be given a permanent banner.
 *
 * Hidden entirely on Teams mobile: the portal is where cards are changed and
 * invoices downloaded, so certification policy 114048 puts it out of bounds
 * there. Cancelling from a phone is a real loss, but the portal is reachable
 * from desktop, web, and the Stripe receipt emails.
 *
 * Shown only for `active` / `past_due`, the two states backed by a real Stripe
 * subscription. `comped` has no Stripe object and `trialing` has nothing to
 * manage yet, so the portal would 409 for both.
 */
export function ManageSubscription() {
  const { entitlement, openBillingPortal } = useBilling();
  const isMobile = useIsMobileClient();

  if (isMobile) return null;
  if (!entitlement?.billingEnabled) return null;
  if (entitlement.status !== 'active' && entitlement.status !== 'past_due') return null;

  return (
    <button
      onClick={() => void openBillingPortal()}
      title="Manage subscription — cancel, change seats, update card, download invoices"
      style={{
        flex: '0 0 auto',
        background: 'none',
        border: 'none',
        padding: '0 4px',
        fontSize: 11,
        lineHeight: 1.4,
        color: 'var(--color-text-secondary, #a1a1aa)',
        textDecoration: 'underline',
        cursor: 'pointer',
      }}
    >
      Manage
    </button>
  );
}
